import { track } from "@/lib/analytics"
import { useCallback, useEffect, useMemo, useRef, useState, type FormEvent } from "react"
import { Link, useLocation, useNavigate, useSearchParams } from "react-router-dom"
import { createCase, deleteCase, getMatterContents, listCases, patchCase, removeSession } from "../api"
import type { CaseFileListRow } from "../contract"
import { NiraBar } from "../ds/NiraBar"
import { usePopover } from "../ds/usePopover"
import { Rail } from "../answer/Rail"
import { Overlay } from "../answer/Overlay"
import { shortDate } from "../answer/format"
import { usePageTitle } from "../answer/hooks"
import { FEATURE_LABEL, FEATURE_TAG, featureOf, type Feature } from "../matter/features"
import "../answer/answer.css"
import "../answer/answer-v4.css"
import "../answer/pro.css"
import "./case.css"
import "./workspaces.css"

const NAME_MAX = 80
const SEARCH_AFTER = 6
const EXAMPLE = "Acme Traders — FY 2023-24 SCN"
const FEATURES: Feature[] = ["research", "notice", "appeal", "oio"]

/** A list row as the server sends it: `features` is every feature used in the workspace, when it says. */
type Row = CaseFileListRow & { features?: string[] }

const n = (count: number, one: string, many = `${one}s`) => `${count} ${count === 1 ? one : many}`

/** The features used in a workspace, in the order the server gives them; the lead one alone when it sends no list. */
function featuresOf(r: Row): Feature[] {
  const tags = r.features?.length ? r.features : [r.feature ?? ""]
  const out: Feature[] = []
  for (const tag of tags) {
    const f = featureOf({ feature: tag, inferred: r.lead === "order" ? "appeal" : r.lead === "notice" ? "notice" : undefined })
    if (!out.includes(f)) out.push(f)
  }
  return out
}

const ARROW = (
  <svg viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M4 12h15M14 7l5 5-5 5" />
  </svg>
)
const DOTS = (
  <svg viewBox="0 0 20 20" width="18" height="18" fill="currentColor" aria-hidden="true">
    <circle cx="4.5" cy="10" r="1.6" /><circle cx="10" cy="10" r="1.6" /><circle cx="15.5" cy="10" r="1.6" />
  </svg>
)

/** The one field that makes a workspace: labelled, with examples, Enter creates, Escape cancels. */
function NameField({ id, busy, error, autoFocus, onCreate, onCancel, inputRef }: {
  id: string
  busy: boolean
  error: string
  autoFocus?: boolean
  onCreate: (title: string) => void
  onCancel?: () => void
  inputRef?: React.Ref<HTMLInputElement>
}) {
  const [value, setValue] = useState("")
  const [touched, setTouched] = useState(false)
  const invalid = touched && !value.trim()
  const submit = (e: FormEvent) => {
    e.preventDefault()
    setTouched(true)
    if (value.trim() && !busy) onCreate(value.trim())
  }
  const message = invalid ? "Give the workspace a name." : error
  return (
    <form className="nws-field" onSubmit={submit} noValidate>
      <label className="nr-field-label" htmlFor={id}>Workspace name</label>
      <div className="nws-field-row">
        <input
          id={id}
          ref={inputRef}
          className="nr-input"
          value={value}
          maxLength={NAME_MAX}
          placeholder={EXAMPLE}
          autoFocus={autoFocus}
          autoComplete="off"
          aria-invalid={message ? true : undefined}
          aria-describedby={`${id}-help`}
          onChange={(e) => { setValue(e.target.value); setTouched(false) }}
          onKeyDown={(e) => { if (e.key === "Escape" && onCancel) { e.stopPropagation(); onCancel() } }}
        />
        <button type="submit" className="ncf-btn primary" disabled={busy}>{busy ? "Creating…" : "Create workspace"}</button>
        {onCancel ? <button type="button" className="ncf-btn" onClick={onCancel}>Cancel</button> : null}
      </div>
      <p id={`${id}-help`} className={message ? "nws-help err" : "nws-help"} role={message ? "alert" : undefined}>
        {message || `A taxpayer or a matter, e.g. ${EXAMPLE}`}
      </p>
    </form>
  )
}

/** First run: the three steps, arrows between them, and the create field already inside step one. */
function FirstRun({ busy, error, onCreate, inputRef }: {
  busy: boolean
  error: string
  onCreate: (title: string) => void
  inputRef: React.Ref<HTMLInputElement>
}) {
  return (
    <section className="nws-first" aria-label="How a workspace works">
      <div className="nws-first-head">
        <h2>Keep one case together</h2>
        <p>Name it once, drop its files once. Every session you start inside it reads the same files.</p>
      </div>
      <ol className="nws-steps">
        <li className="nws-step lead">
          <span className="nws-num" aria-hidden="true">1</span>
          <h3>Name a workspace</h3>
          <NameField id="nws-name-first" busy={busy} error={error} onCreate={onCreate} inputRef={inputRef} />
        </li>
        <li className="nws-arrow" aria-hidden="true">{ARROW}</li>
        <li className="nws-step">
          <span className="nws-num" aria-hidden="true">2</span>
          <h3>Drop its files</h3>
          <p>Add them once, in one place.</p>
          <ul className="nws-chips" aria-label="Files you can add">
            {["The notice", "Your reply", "Invoices", "GSTR-2B and 3B", "The order"].map((f) => <li key={f}>{f}</li>)}
          </ul>
        </li>
        <li className="nws-arrow" aria-hidden="true">{ARROW}</li>
        <li className="nws-step">
          <span className="nws-num" aria-hidden="true">3</span>
          <h3>Start sessions inside it</h3>
          <p>Each one reads the same files.</p>
          <ul className="nws-chips" aria-label="Sessions you can start">
            {FEATURES.map((f) => <li key={f}>{FEATURE_LABEL[f]}</li>)}
          </ul>
        </li>
      </ol>
    </section>
  )
}

/** One workspace: name, counts, the features used, last updated, and a menu. */
function WorkspaceRow({ row, onRename, onDelete }: {
  row: Row
  onRename: (id: string, title: string) => void
  onDelete: (row: Row) => void
}) {
  const { open, setOpen, button, panel } = usePopover()
  const [renaming, setRenaming] = useState(false)
  const [draft, setDraft] = useState(row.title)
  const input = useRef<HTMLInputElement>(null)
  useEffect(() => { if (renaming) input.current?.select() }, [renaming])
  const save = () => {
    const next = draft.trim()
    setRenaming(false)
    if (next && next !== row.title) onRename(row.id, next)
  }
  const tags = featuresOf(row)
  return (
    <li className="nws-row">
      {renaming ? (
        <form className="nws-rename" onSubmit={(e) => { e.preventDefault(); save() }}>
          <input
            ref={input}
            className="nr-input"
            aria-label="Workspace name"
            value={draft}
            maxLength={NAME_MAX}
            autoFocus
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Escape") { e.stopPropagation(); setRenaming(false) } }}
          />
          <button type="submit" className="ncf-btn primary" disabled={!draft.trim()}>Save</button>
          <button type="button" className="ncf-btn" onClick={() => setRenaming(false)}>Cancel</button>
        </form>
      ) : (
        <Link className="nws-main" to={`/workspace/${encodeURIComponent(row.id)}`}>
          <span className="nws-name">{row.title}</span>
          <span className="nws-meta">
            {n(row.documents, "file")} · {n(row.threads, "session")} · updated {shortDate(row.updated_at)}
          </span>
          <span className="nws-tags">{tags.map((f) => <span key={f} className="nws-tag" data-feature={FEATURE_TAG[f]}>{FEATURE_LABEL[f]}</span>)}</span>
        </Link>
      )}
      {renaming ? null : (
        <div className="nr-ctx nws-more">
          <button ref={button} type="button" className="nws-dots" aria-label={`Actions for ${row.title}`} aria-haspopup="menu" aria-expanded={open} onClick={() => setOpen((v) => !v)}>{DOTS}</button>
          {open ? (
            <div ref={panel} className="nr nr-menu nr-menu-end" role="menu" aria-label={`Actions for ${row.title}`}>
              <button type="button" role="menuitem" className="nr-menu-item" onClick={() => { setOpen(false); setDraft(row.title); setRenaming(true) }}>Rename</button>
              <button type="button" role="menuitem" className="nr-menu-item nws-danger" onClick={() => { setOpen(false); onDelete(row) }}>Delete</button>
            </div>
          ) : null}
        </div>
      )}
    </li>
  )
}

function ConfirmDelete({ row, onCancel, onConfirm }: { row: Row; onCancel: () => void; onConfirm: () => void }) {
  const what = row.threads ? `Its files and ${n(row.threads, "session")} are removed.` : "Its files are removed."
  return (
    <Overlay label={`Delete ${row.title}`} onClose={onCancel}>
      <h2 className="nr-ov-h">Delete {row.title}?</h2>
      <p className="nr-ov-p">{what} This can't be undone.</p>
      <div className="nws-confirm">
        <button type="button" className="ncf-btn" onClick={onCancel}>Keep it</button>
        <button type="button" className="ncf-btn danger" onClick={onConfirm}>Delete workspace</button>
      </div>
    </Overlay>
  )
}

/** Every workspace, newest first. New readers get the three-step guide with the create field inside it. */
export function CasesPage() {
  const [rows, setRows] = useState<Row[] | null>(null)
  const [histOpen, setHistOpen] = useState(false)
  const navigate = useNavigate()
  const here = useLocation()
  const [params] = useSearchParams()
  const [naming, setNaming] = useState(Boolean(params.get("new")))
  const [creating, setCreating] = useState(false)
  const [createError, setCreateError] = useState("")
  const [pending, setPending] = useState<Row | null>(null)
  const [notice, setNotice] = useState("")
  const [query, setQuery] = useState("")
  const firstInput = useRef<HTMLInputElement>(null)
  usePageTitle("Workspaces")
  useEffect(() => {
    listCases().then((r) => setRows(r as Row[])).catch(() => setRows([]))
  }, [])

  const create = useCallback(async (title: string) => {
    if (creating) return
    setCreating(true)
    setCreateError("")
    try {
      const made = await createCase({ title })
      track("matter_created", { from: rows && rows.length === 0 ? "empty_state" : "list" })
      navigate(`/workspace/${encodeURIComponent(made.id)}`)
    } catch {
      setCreateError("Couldn't create the workspace. Try again.")
      setCreating(false)
    }
  }, [creating, navigate, rows])

  const rename = async (id: string, title: string) => {
    const before = rows
    setNotice("")
    setRows((cur) => cur && cur.map((r) => (r.id === id ? { ...r, title } : r)))
    try {
      await patchCase(id, { title })
    } catch {
      setRows(before)
      setNotice("Couldn't rename the workspace. Try again.")
    }
  }

  // Optimistic: the row leaves at once and comes back, in place, if the server refuses.
  const remove = async (row: Row) => {
    setPending(null)
    setNotice("")
    const before = rows
    setRows((cur) => cur && cur.filter((r) => r.id !== row.id))
    try {
      // Order drafts are unlinked, not deleted: they keep their own files. A draft that will not unlink never blocks the delete.
      const contents = await getMatterContents(row.id).catch(() => null)
      const drafts = (contents?.sessions ?? []).filter((s) => s.type !== "nira")
      await Promise.all(drafts.map((d) => removeSession(row.id, d.type, d.id).catch(() => {})))
      await deleteCase(row.id)
    } catch (err) {
      setRows(before)
      setNotice((err as { status?: number })?.status === 409
        ? `An answer is still being written in ${row.title}. Delete it once that finishes.`
        : `Couldn't delete ${row.title}. Try again.`)
    }
  }

  const sorted = useMemo(() => (rows ?? []).slice().sort((a, b) => b.updated_at.localeCompare(a.updated_at)), [rows])
  const shown = useMemo(() => {
    const q = query.trim().toLowerCase()
    return q ? sorted.filter((r) => r.title.toLowerCase().includes(q)) : sorted
  }, [sorted, query])
  const empty = rows !== null && rows.length === 0

  return (
    <div className="na2 na4 ncf">
      <NiraBar onMenu={() => setHistOpen((v) => !v)} menuOpen={histOpen} menuControls="nira-hlist" crumbs={[{ label: "Home", to: "/ask" }, { label: "Workspaces" }]} />
      <Rail threadId="" histOpen={histOpen} onHist={setHistOpen} refreshKey={0} drawerOnly />
      <div className={`scrim${histOpen ? " on" : ""}`} aria-hidden="true" onClick={() => setHistOpen(false)} />
      <main className="ncf-main">
        <header className="ncf-head">
          <div className="ncf-title">
            <h1>Workspaces</h1>
            <Link className="nws-about" to="/workspaces/about" state={{ from: here.pathname + here.search }}>What is a workspace?</Link>
          </div>
          <div className="ncf-acts">
            <button
              type="button"
              className="ncf-btn primary"
              onClick={() => (empty ? firstInput.current?.focus() : setNaming(true))}
            >
              New workspace
            </button>
          </div>
        </header>
        {rows === null ? <p className="ncf-msg" role="status">Loading…</p> : null}
        {empty ? <FirstRun busy={creating} error={createError} onCreate={(t) => void create(t)} inputRef={firstInput} /> : null}
        {!empty && naming ? (
          <section className="nws-new" aria-label="New workspace">
            <NameField id="nws-name" busy={creating} error={createError} autoFocus onCreate={(t) => void create(t)} onCancel={() => { setNaming(false); setCreateError("") }} />
          </section>
        ) : null}
        {notice ? <p className="nws-notice" role="alert">{notice}</p> : null}
        {rows?.length ? (
          <section className="nws-list" aria-label="Your workspaces">
            {rows.length > SEARCH_AFTER ? (
              <input type="search" className="nr-input nws-search" aria-label="Search workspaces" placeholder="Search workspaces" value={query} onChange={(e) => setQuery(e.target.value)} />
            ) : null}
            {shown.length ? (
              <ul className="nws-rows">
                {shown.map((r) => <WorkspaceRow key={r.id} row={r} onRename={(id, t) => void rename(id, t)} onDelete={setPending} />)}
              </ul>
            ) : (
              <p className="ncf-empty">No workspace matches "{query.trim()}".</p>
            )}
          </section>
        ) : null}
      </main>
      {pending ? <ConfirmDelete row={pending} onCancel={() => setPending(null)} onConfirm={() => void remove(pending)} /> : null}
    </div>
  )
}
